import { useEffect, useState } from "react";
import { BadgeCheck, Power, Search, Star, Trash2, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { api, fmtDate, fmtTL } from "../../lib/api";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import CriticalPinDialog from "../CriticalPinDialog";

const Chip = ({ active, onClick, children, tid }) => (
    <button onClick={onClick} data-testid={tid} className={`rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors ${active ? "border-primary bg-primary text-primary-foreground" : "hover:border-terra-500/50"}`}>{children}</button>
);

export default function SuppliersTab() {
    const [suppliers, setSuppliers] = useState(null);
    const [busy, setBusy] = useState(null);
    const [supFilter, setSupFilter] = useState("all");
    const [supSearch, setSupSearch] = useState("");
    const [pinAction, setPinAction] = useState(null);

    const load = () => api.get("/admin/suppliers").then((r) => setSuppliers(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);

    const toggleVerify = async (s) => {
        setBusy(s.supplier_id);
        try {
            await api.post(`/admin/suppliers/${s.supplier_id}/verify`, { verified: !s.verified });
            toast.success(`${s.name} ${!s.verified ? "doğrulandı" : "doğrulaması kaldırıldı"}`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "İşlem başarısız");
        } finally {
            setBusy(null);
        }
    };

    const toggleActive = async (s, pin) => {
        if (s.active && pin === undefined) {
            setPinAction({ label: `"${s.name}" tedarikçisini pasifleştirme (tüm teklifleri kapatılacak)`, run: (p) => toggleActive(s, p) });
            return;
        }
        setBusy(s.supplier_id);
        try {
            await api.post(`/admin/suppliers/${s.supplier_id}/status`, { active: !s.active }, pin ? { headers: { "X-Critical-Pin": pin } } : {});
            toast.success(`${s.name} ${!s.active ? "aktifleştirildi" : "pasifleştirildi — teklifleri kapatıldı"}`);
            setPinAction(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "İşlem başarısız");
        } finally {
            setBusy(null);
        }
    };

    const deleteSupplier = async (s, pin) => {
        if (pin === undefined) {
            setPinAction({ label: `"${s.name}" tedarikçisini kalıcı olarak silme (kullanıcıları ve tüm teklifleri de silinecek)`, run: (p) => deleteSupplier(s, p) });
            return;
        }
        setBusy(s.supplier_id);
        try {
            await api.delete(`/admin/records/suppliers/${s.supplier_id}`, { headers: { "X-Critical-Pin": pin } });
            toast.success(`${s.name} kalıcı olarak silindi`);
            setPinAction(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Silinemedi");
        } finally {
            setBusy(null);
        }
    };

    const saveTerm = async (s, val) => {
        const days = parseInt(val, 10);
        if (isNaN(days) || days === (s.payment_term_days ?? 30)) return;
        try {
            await api.patch(`/admin/suppliers/${s.supplier_id}/payment-term`, { days });
            toast.success(`${s.name} vade süresi ${days} gün olarak güncellendi`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Vade güncellenemedi");
        }
    };

    const [supSort, setSupSort] = useState("name");

    const filtered = (suppliers || []).filter((s) => {
        const q = supSearch.toLowerCase();
        const matchesQ = !q || s.name.toLowerCase().includes(q) || s.city.toLowerCase().includes(q);
        const matchesF = supFilter === "all" ? true : supFilter === "active" ? s.active : supFilter === "passive" ? !s.active : supFilter === "verified" ? s.verified : !s.verified;
        return matchesQ && matchesF;
    }).sort((a, b) =>
        supSort === "name" ? a.name.localeCompare(b.name, "tr")
        : supSort === "trust" ? (b.trust_score || 0) - (a.trust_score || 0)
        : supSort === "revenue" ? (b.total_spend || b.total_sales || 0) - (a.total_spend || a.total_sales || 0)
        : supSort === "orders" ? (b.order_count || 0) - (a.order_count || 0)
        : new Date(b.last_order_at || 0) - new Date(a.last_order_at || 0)
    );

    return (
        <div data-testid="cari-suppliers-tab">
            <div className="mb-4 flex flex-wrap items-center gap-2">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                    <Input value={supSearch} onChange={(e) => setSupSearch(e.target.value)} placeholder="Tedarikçi veya şehir ara..." className="h-9 w-64 pl-8 text-sm" data-testid="cari-supplier-search" />
                </div>
                <Chip active={supFilter === "all"} onClick={() => setSupFilter("all")} tid="cari-sup-filter-all">Tümü</Chip>
                <Chip active={supFilter === "active"} onClick={() => setSupFilter("active")} tid="cari-sup-filter-active">Aktif</Chip>
                <Chip active={supFilter === "passive"} onClick={() => setSupFilter("passive")} tid="cari-sup-filter-passive">Pasif</Chip>
                <Chip active={supFilter === "verified"} onClick={() => setSupFilter("verified")} tid="cari-sup-filter-verified">Doğrulanmış</Chip>
                <select value={supSort} onChange={(e) => setSupSort(e.target.value)} className="h-9 rounded-md border bg-background px-2 text-xs" data-testid="cari-sup-sort">
                    <option value="name">Ad (A-Z)</option>
                    <option value="trust">Güven Skoru</option>
                    <option value="revenue">Toplam Satış</option>
                    <option value="orders">Sipariş Sayısı</option>
                    <option value="recent">Son Sipariş</option>
                </select>
                <span className="ml-auto text-xs text-muted-foreground">{filtered.length} kayıt</span>
            </div>
            {!suppliers ? (
                <div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-20 animate-pulse rounded-2xl bg-muted" />)}</div>
            ) : (
                <div className="overflow-hidden rounded-2xl border bg-card shadow-sm">
                    <div className="max-h-[520px] overflow-x-auto overflow-y-auto">
                        <table className="w-full text-sm" data-testid="cari-suppliers-table">
                            <thead className="sticky top-0 bg-muted/80 backdrop-blur">
                                <tr className="border-b text-left text-xs uppercase tracking-wider text-muted-foreground">
                                    <th className="p-4">Tedarikçi</th><th className="p-4">Güven</th><th className="p-4">Ürün / Stok</th><th className="p-4">Sipariş</th><th className="p-4">Toplam Satış</th><th className="p-4">Son Sipariş</th><th className="p-4">Vade</th><th className="p-4">Durum</th><th className="p-4 text-right">İşlemler</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((s) => (
                                    <tr key={s.supplier_id} className={`border-b last:border-0 hover:bg-muted/40 ${!s.active ? "opacity-55" : ""}`} data-testid={`cari-supplier-row-${s.supplier_id}`}>
                                        <td className="p-4">
                                            <p className="font-semibold">{s.name}</p>
                                            <p className="text-xs text-muted-foreground">{s.city} • {s.contacts.map((c) => c.email).join(", ") || "kullanıcı yok"}</p>
                                        </td>
                                        <td className="p-4"><span className="inline-flex items-center gap-1 font-semibold text-sage-600"><Star className="h-3.5 w-3.5 fill-saffron-400 text-saffron-400" />{s.trust_score}</span></td>
                                        <td className="p-4 text-muted-foreground">{s.product_count} ürün • {s.total_stock.toLocaleString("tr-TR")} stok</td>
                                        <td className="p-4">{s.order_count}{s.open_orders > 0 && <Badge variant="outline" className="ml-1.5 border-amber-200 bg-amber-100 text-[10px] text-amber-800">{s.open_orders} açık</Badge>}</td>
                                        <td className="p-4 font-display font-extrabold text-terra-600">{fmtTL(s.total_sales)}</td>
                                        <td className="p-4 text-muted-foreground">{fmtDate(s.last_order_at)}</td>
                                        <td className="p-4" data-testid={`payment-term-cell-${s.supplier_id}`}>
                                            <div className="flex items-center gap-1.5">
                                                <Input
                                                    type="number" min={0} max={365}
                                                    defaultValue={s.payment_term_days ?? 30}
                                                    className="h-8 w-16 text-xs"
                                                    onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                                                    onBlur={(e) => saveTerm(s, e.target.value)}
                                                    data-testid={`payment-term-input-${s.supplier_id}`}
                                                />
                                                <span className="text-[10px] text-muted-foreground">gün vade</span>
                                            </div>
                                        </td>
                                        <td className="p-4">
                                            <div className="flex flex-col gap-1">
                                                {s.verified ? (
                                                    <Badge variant="outline" className="w-fit border-blue-200 bg-blue-100 text-[10px] text-blue-800"><BadgeCheck className="mr-1 h-3 w-3" />Doğrulanmış</Badge>
                                                ) : (
                                                    <Badge variant="outline" className="w-fit border-stone-200 bg-stone-100 text-[10px] text-stone-600">Doğrulanmadı</Badge>
                                                )}
                                                <Badge variant="outline" className={`w-fit text-[10px] ${s.active ? "border-emerald-200 bg-emerald-100 text-emerald-800" : "border-red-200 bg-red-100 text-red-700"}`}>{s.active ? "Aktif" : "Pasif"}</Badge>
                                            </div>
                                        </td>
                                        <td className="p-4">
                                            <div className="flex justify-end gap-2">
                                                <Button size="sm" variant="outline" disabled={busy === s.supplier_id} onClick={() => toggleVerify(s)} data-testid={`cari-verify-${s.supplier_id}`}>
                                                    <UserCheck className="mr-1.5 h-3.5 w-3.5" /> {s.verified ? "Doğrulamayı Kaldır" : "Doğrula"}
                                                </Button>
                                                <Button size="sm" variant="outline" className={s.active ? "text-destructive" : "text-emerald-700"} disabled={busy === s.supplier_id} onClick={() => toggleActive(s)} data-testid={`cari-active-toggle-${s.supplier_id}`}>
                                                    {s.active ? "Pasifleştir" : "Aktifleştir"}
                                                </Button>
                                                <Button size="sm" variant="outline" className="text-destructive" disabled={busy === s.supplier_id} onClick={() => deleteSupplier(s)} data-testid={`cari-supplier-delete-${s.supplier_id}`}>
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
            <CriticalPinDialog open={!!pinAction} onClose={() => setPinAction(null)} onSubmit={(pin) => pinAction?.run(pin)} actionLabel={pinAction?.label || ""} busy={!!busy} />
        </div>
    );
}
